import React from "react";
import { connect } from "react-redux";

import { loadCategory } from "../../../api/products";
import { LoadingSpinner } from "../../../common/LoadingSpinner";
import { IProduct } from "../../../models/catalogue.interfaces";
import { IProductCategoryID } from "../../../models/nominals";
import { BreakPoint } from "../../../models/screen.interfaces";
import { getViewportBreakpoint } from "../../../utils/detectMobile";
import { TDispatchMapper, TStateMapper } from "../../reducers.interfaces";
import { GridContent } from "../elements/GridContent";
import { SideNav } from "../elements/SideNav";
import { TopNav } from "../elements/TopNav";
import { GridFilterConfig } from "../filters";
import { startHistoryListener } from "../history";
import {
    Action,
    IActionProductsLoadingDone,
    IActionProductsLoadingStarted,
} from "../reducers.interfaces";
import { GridSorterConfig } from "../sorters";

import styles from "./ProductGrid.module.scss";

interface IOwnProps {
    // TODO(i18n): gridVariant should be a technical key, but verify it's not displayed to users
    gridVariant: string;
    productCategoryID: IProductCategoryID;
    filters: GridFilterConfig[];
    sorters?: GridSorterConfig[];
    hideSort?: boolean;
    disableHoverOverlay?: boolean;
    buildExternalCallout?: (rootProduct: IProduct) => React.ReactNode;
}

interface IReduxProps {
    isLoading: boolean;
}

interface IDispatchProps {
    onDidMount: () => void;
    onProductsLoadingStarted: () => void;
    onProductsLoadingDone: (products: IProduct[]) => void;
}

interface IProps extends IOwnProps, IReduxProps, IDispatchProps {}

interface IState {}

// TODO(ssr, LEVEL 5 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 5/10 due to PureComponent optimization, Redux connection, lifecycle method for analytics, and conditional rendering based on filters/products.
export class ProductGridComponent extends React.PureComponent<IProps, IState> {
    componentDidMount() {
        this.props.onDidMount();
        this.loadProducts(this.props.productCategoryID);
    }

    componentDidUpdate(prevProps: IProps) {
        if (prevProps.productCategoryID !== this.props.productCategoryID) {
            this.loadProducts(this.props.productCategoryID);
        }
    }

    private async loadProducts(productCategoryID: IProductCategoryID) {
        this.props.onProductsLoadingStarted();
        const products = await loadCategory(productCategoryID);
        this.props.onProductsLoadingDone(products);
    }

    private readonly isMobileOrTabletWidth = () => {
        const currentBreakpoint = getViewportBreakpoint();
        return currentBreakpoint < BreakPoint.LARGE;
    };

    render() {
        let content: React.ReactNode | null = null;
        if (this.props.isLoading) {
            content = <LoadingSpinner />;
        } else {
            content = (
                <>
                    <TopNav
                        gridVariant={this.props.gridVariant}
                        filters={this.props.filters}
                        sorters={this.props.sorters}
                        hideSort={this.props.hideSort}
                    />
                    <div className={styles.contentContainer}>
                        {!this.isMobileOrTabletWidth() && (
                            <SideNav
                                gridVariant={this.props.gridVariant}
                                filters={this.props.filters}
                            />
                        )}
                        {/* TODO(i18n): cardType="grid-card" should be a technical key, but verify it's not displayed to users */}
                        <GridContent
                            gridVariant={this.props.gridVariant}
                            cardType="grid-card"
                            filters={this.props.filters}
                            sorters={this.props.sorters}
                            disableHoverOverlay={this.props.disableHoverOverlay}
                            buildExternalCallout={
                                this.props.buildExternalCallout
                            }
                        />
                    </div>
                </>
            );
        }
        return <div className={styles.container}>{content}</div>;
    }
}

const mapStateToProps: TStateMapper<"productgrid2", IReduxProps, IOwnProps> = (
    rootState,
    ownProps,
) => {
    return {
        ...ownProps,
        isLoading: rootState.productgrid2?.isLoading,
    };
};

const mapDispatchToProps: TDispatchMapper<IDispatchProps> = (dispatch) => {
    return {
        onDidMount: () => {
            startHistoryListener(dispatch);
        },
        onProductsLoadingStarted: () => {
            dispatch<IActionProductsLoadingStarted>({
                type: Action.PRODUCTS_LOADING_STARTED,
            });
        },
        onProductsLoadingDone: (products) => {
            dispatch<IActionProductsLoadingDone>({
                type: Action.PRODUCTS_LOADING_DONE,
                payload: products,
            });
        },
    };
};

export const ProductGrid = connect(
    mapStateToProps,
    mapDispatchToProps,
)(ProductGridComponent);
